import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Check, ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { draftPath } from "../workshop/draftText";
import {
  createdText,
  DONE,
  DOT_CLASS,
  DOT_TEXT,
  dotText,
  HANDLED,
  KINDS,
  SCORE_WEIGHTS,
  shortDate,
  similarPapers,
  SOURCES,
} from "./ideaText";
import type { Evidence, IdeaCard, IdeaChange, PaperMatch } from "./types";

/** Score with the explanation of score and dot; hover on desktop, tap on the phone. */
const ScoreInfo = ({ value }: { value: number }) => {
  const [open, setOpen] = useState(false);
  const dot = (cls: string, text: string) => (
    <li className="flex items-center gap-2">
      <span className={cn("size-2.5 shrink-0 rounded-full", cls)} />
      {text}
    </li>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={`Score ${value} von 100, Erklärung anzeigen`}
        className="shrink-0 cursor-help rounded-md text-right"
        onPointerEnter={(e) => e.pointerType === "mouse" && setOpen(true)}
        onPointerLeave={(e) => e.pointerType === "mouse" && setOpen(false)}
      >
        <span className="block text-2xl leading-8 font-semibold text-primary tabular-nums">
          {value}
        </span>
        <span className="block text-[11px] text-muted-foreground">von 100</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 text-xs">
        <p className="font-semibold">Score 0–100</p>
        <p className="mt-1 text-muted-foreground">
          Summe aus fünf Teilen, je mit Höchstpunktzahl:
        </p>
        <ul className="mt-1 grid grid-cols-2 gap-x-3 text-muted-foreground">
          {SCORE_WEIGHTS.map(([name, max]) => (
            <li key={name} className="flex justify-between">
              <span>{name}</span>
              <span className="tabular-nums">{max}</span>
            </li>
          ))}
        </ul>
        <p className="mt-1 text-muted-foreground">
          Abzug, wenn die BV das Thema schon befasst hat; höchstens 25, wenn es
          erledigt ist.
        </p>
        <p className="mt-3 font-semibold">Punkt am Titel</p>
        <p className="mt-1 text-muted-foreground">
          Abgleich mit BV-Drucksachen und Baustellen:
        </p>
        <ul className="mt-1 space-y-0.5 text-muted-foreground">
          {dot(DOT_CLASS.gruen, DOT_TEXT.gruen)}
          {dot(DOT_CLASS.gelb, DOT_TEXT.gelb)}
          {dot(DOT_CLASS.rot, DOT_TEXT.rot)}
          {dot("bg-muted-foreground/40", "nicht geprüft")}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

const PaperLink = ({
  url,
  children,
}: {
  url: string | null;
  children: string;
}) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="shrink-0 text-primary underline"
    >
      {children}
    </a>
  ) : (
    <span className="shrink-0">{children}</span>
  );

const Papers = ({ matches, text }: { matches: PaperMatch[]; text: string }) => (
  <div className="mt-3 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
    <p className="text-muted-foreground">{text}</p>
    <ul className="mt-1 space-y-0.5">
      {matches.map((t) => (
        <li key={t.nummer} className="flex min-w-0 gap-2">
          <PaperLink url={t.url}>{t.nummer}</PaperLink>
          {t.datum && (
            <span className="shrink-0 text-muted-foreground tabular-nums">
              {shortDate(t.datum)}
            </span>
          )}
          <span className="truncate" title={t.titel}>
            {t.titel}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

/** Papers and road works behind an "already done" verdict, with the model's reason. */
const DoneBox = ({
  urteil,
  belege,
  grund,
}: {
  urteil: string;
  belege: Evidence[];
  grund: string | null;
}) => (
  <div className="mt-3 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
    <p className="text-muted-foreground">
      Umsetzung: {DONE[urteil] ?? urteil}
      {grund ? ` – ${grund}` : ""}
    </p>
    {belege.length > 0 && (
      <ul className="mt-1 space-y-0.5">
        {belege.map((b) => {
          const works = b.status === "Baustelle";
          return (
            <li key={b.nummer} className="flex min-w-0 gap-2">
              <PaperLink url={b.url}>
                {works ? "Baustelle" : b.nummer}
              </PaperLink>
              {b.datum && (
                <span className="shrink-0 text-muted-foreground tabular-nums">
                  {works ? "bis " : ""}
                  {shortDate(b.datum)}
                </span>
              )}
              <span className="truncate" title={b.titel ?? undefined}>
                {b.titel}
              </span>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

/** Contribution text for cards without a pitch, three lines until "mehr". */
const OriginalText = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
      <p className="text-xs text-muted-foreground">
        Originalbeitrag (kein Pitch)
      </p>
      <p className={cn("mt-1 whitespace-pre-line", !open && "line-clamp-3")}>
        {text}
      </p>
      {text.length > 240 && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="mt-1 text-xs font-medium text-primary underline"
        >
          {open ? "weniger" : "mehr"}
        </button>
      )}
    </div>
  );
};

export const IdeaCardView = ({
  card: k,
  onChange,
  onHandover,
  onBundle,
}: {
  card: IdeaCard;
  /** A function change is computed from the last saved state, so a double click toggles twice. */
  onChange: (
    k: IdeaCard,
    change: IdeaChange | ((saved: IdeaCard) => IdeaChange),
  ) => Promise<void>;
  onHandover: (k: IdeaCard) => Promise<void>;
  onBundle?: (id: number) => void;
}) => {
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState(k.entscheidung_notiz ?? "");
  // Follow the saved note, but never over unsaved typing in the field.
  const typing = useRef(false);
  useEffect(() => {
    if (!typing.current) setNote(k.entscheidung_notiz ?? "");
  }, [k.entscheidung_notiz]);
  const v2 = k.score_details?.raster === "v2";
  const papers = similarPapers(k);
  const done = k.score_details?.erledigt;
  const kind = KINDS[k.art ?? ""];
  const discarded = k.entscheidung === "verworfen";
  return (
    <article className="rounded-xl border bg-card p-5 shadow-xs">
      <div className="flex items-start gap-3">
        {/* As tall as the title's first line, so the dot sits on its middle. */}
        <span className="flex h-6 shrink-0 items-center" title={dotText(k)}>
          <span
            className={cn(
              "size-2.5 rounded-full",
              DOT_CLASS[k.ampel ?? ""] ?? "bg-muted-foreground/40",
            )}
          />
        </span>
        <div className="min-w-0 flex-1">
          <h2
            className={cn(
              "leading-6 font-semibold",
              k.titel_ersatz && "italic",
            )}
            title={
              k.titel_ersatz
                ? "Kein eigener Titel, Anfang des Pitch"
                : undefined
            }
          >
            {k.titel}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {[
              SOURCES[k.typ],
              k.stadtteil,
              k.ausschuss ?? "Ausschuss offen",
              createdText(k),
            ]
              .filter(Boolean)
              .join(" · ")}
            {kind && (
              <>
                {" · "}
                <span
                  title={k.score_details?.ziel_grund ?? undefined}
                  className={
                    k.score_details?.ziel_grund
                      ? "cursor-help underline decoration-dotted underline-offset-2"
                      : undefined
                  }
                >
                  Empfehlung: {kind}
                </span>
              </>
            )}
            {k.quelle_url && (
              <>
                {" · "}
                <a
                  href={k.quelle_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 text-primary underline"
                >
                  Originalbeitrag
                  <ExternalLink aria-hidden className="size-3" />
                </a>
              </>
            )}
          </p>
        </div>
        <ScoreInfo value={k.score} />
      </div>
      {k.geaendert_seit_entscheidung && (
        <p className="mt-3 rounded-md bg-warning-soft px-3 py-1.5 text-xs text-warning-text">
          Beitrag geändert seit der Entscheidung
        </p>
      )}
      {k.pitch ? (
        <p className="mt-3 text-sm">{k.pitch}</p>
      ) : (
        k.original && <OriginalText text={k.original} />
      )}
      {papers.length > 0 && (
        <Papers
          matches={papers}
          text={
            v2
              ? `Ähnliche Drucksachen – BV: ${HANDLED[k.dopplung?.urteil ?? ""] ?? "unklar"}`
              : dotText(k)
          }
        />
      )}
      {done && (done.urteil === "erledigt" || done.urteil === "teilweise") && (
        <DoneBox {...done} />
      )}
      {onBundle && !!k.buendel_weitere && (
        <button
          type="button"
          onClick={() => onBundle(k.id)}
          className="mt-3 text-xs font-medium text-primary underline"
        >
          +{k.buendel_weitere}{" "}
          {k.buendel_weitere === 1 ? "ähnlicher Beitrag" : "ähnliche Beiträge"}{" "}
          am selben Ort
        </button>
      )}
      <dl className="mt-3 text-xs">
        <dt className="text-muted-foreground">Nutzen</dt>
        <dd>{[k.nutzen, k.nutzen_text].filter(Boolean).join(" – ") || "–"}</dd>
      </dl>
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
        {k.entwurf_id ? (
          <Button
            asChild
            className="basis-full bg-success text-white hover:bg-success/90 sm:basis-auto"
          >
            <Link to={draftPath(k.entwurf_id)}>
              <Check aria-hidden />
              Zum Entwurf
            </Link>
          </Button>
        ) : (
          <Button
            disabled={sending}
            onClick={() => {
              setSending(true);
              void onHandover(k).finally(() => setSending(false));
            }}
            className="basis-full sm:basis-auto"
            title="Startet einen Werkstatt-Entwurf aus Titel, Pitch, Ort und Link"
          >
            {sending ? "Startet …" : "In Werkstatt"}
          </Button>
        )}
        <Button
          variant={k.gemerkt ? "secondary" : "outline"}
          aria-pressed={k.gemerkt}
          onClick={() => void onChange(k, (s) => ({ gemerkt: !s.gemerkt }))}
          className="flex-1 sm:flex-none"
        >
          {k.gemerkt ? "✓ Gemerkt" : "Merken"}
        </Button>
        <Button
          variant="outline"
          aria-pressed={discarded}
          onClick={() =>
            void onChange(k, (s) => ({
              entscheidung:
                s.entscheidung === "verworfen" ? "offen" : "verworfen",
            }))
          }
          className={cn(
            "flex-1 sm:flex-none",
            discarded && "border-warning bg-warning-soft text-warning-text",
          )}
          title={
            discarded ? "Klick holt die Idee zurück in „offen“" : undefined
          }
        >
          {discarded ? "✓ Verworfen" : "Verwerfen"}
        </Button>
        <Input
          value={note}
          onChange={(e) => {
            typing.current = true;
            setNote(e.target.value);
          }}
          onBlur={() => {
            typing.current = false;
            if (note !== (k.entscheidung_notiz ?? ""))
              void onChange(k, { notiz: note || null });
          }}
          placeholder="Notiz"
          aria-label="Notiz"
          className="min-w-0 basis-full sm:ml-auto sm:basis-0 sm:flex-1"
        />
      </div>
    </article>
  );
};
